/**
 * apps/web/app/(public)/_actions/set-locale.ts
 *
 * Server Action — toggle PT⇆ES via cookie `brisa_locale` + redirect HTTP 303
 * nativo. Decisão D4 do ADR `legal-documents-v1`.
 *
 * Validações (AR-8 do ADR):
 *   - locale ∈ {'pt', 'es'} — qualquer outro valor → throw 'invalid locale'
 *   - returnTo deve começar com '/' e NÃO conter '://' nem path-traversal
 *     ('../') — qualquer outro valor → throw 'invalid returnTo'
 *
 * Cookie attrs:
 *   - sameSite: 'lax' (alinhado a `_actions.ts:140` do autenticacao-onboarding)
 *   - path: '/'
 *   - maxAge: 1 ano (LOCALE_COOKIE_MAX_AGE de @brisa/shared/i18n)
 *   - secure: true em production (NODE_ENV)
 *
 * AGR-2026-002: redirect HTTP nativo via Server Action — emite 303 Location
 * (não payload RSC). Smoke E2E em PRD-mode valida follow-redirect.
 */
'use server';

import { cookies } from 'next/headers';
import { redirect } from 'next/navigation';
import { env } from '@brisa/shared/env';
import { LOCALE_COOKIE, LOCALE_COOKIE_MAX_AGE } from '@brisa/shared/i18n';
import { isEnabled } from '@brisa/shared/feature-flags';
import { logLegalLocaleToggled } from '@brisa/shared/observability';

export type SupportedLocale = 'pt' | 'es';

/**
 * Validação anti open-redirect — `returnTo` deve ser um path absoluto local.
 * Rejeita: URLs absolutas (https://...), schemes (javascript:, data:),
 * path traversal (../), e strings vazias.
 */
function isSafeReturnTo(returnTo: string): boolean {
  if (typeof returnTo !== 'string' || returnTo.length === 0) return false;
  if (!returnTo.startsWith('/')) return false;
  if (returnTo.includes('://')) return false;
  if (returnTo.includes('..')) return false;
  // Reject protocol-relative URLs (`//evil.com/path`)
  if (returnTo.startsWith('//')) return false;
  // Reject script schemes
  if (/^[a-z]+:/i.test(returnTo)) return false;
  return true;
}

export async function setLocaleAction(
  locale: SupportedLocale,
  returnTo: string,
): Promise<void> {
  if (locale !== 'pt' && locale !== 'es') {
    throw new Error('invalid locale');
  }
  if (!isSafeReturnTo(returnTo)) {
    throw new Error('invalid returnTo');
  }

  const cookieJar = cookies();
  // Resolve locale anterior antes do overwrite (best-effort — undefined se
  // primeira navegação do usuário).
  const previousLocale = cookieJar.get(LOCALE_COOKIE)?.value;
  const fromLocale: SupportedLocale =
    previousLocale === 'es' ? 'es' : 'pt'; // default PT alinhado com resolveLocale()

  cookieJar.set(LOCALE_COOKIE, locale, {
    maxAge: LOCALE_COOKIE_MAX_AGE,
    path: '/',
    sameSite: 'lax',
    secure: env.NODE_ENV === 'production',
    httpOnly: false, // cookie é lido por Server Components — middleware/SSR only
  });

  // METRICS_SPEC §3 Evento 3 — server-side log do toggle (P2). Sink: logger.
  // GA4/Clarity ficam por conta do próximo page-view (que dispara
  // legal_document_viewed com o novo locale, fechando a triangulação).
  // Erro de telemetria nunca quebra UX — wrapped em try/catch implícito do logger.
  const isAuthenticated = Array.from(cookieJar.getAll()).some((c) =>
    c.name.startsWith('sb-'),
  );
  const session_id = cookieJar.get('brisa_sid')?.value ?? 'unknown';
  const user_id = isAuthenticated ? 'authenticated' : 'anonymous';
  const featureFlagActive = await isEnabled('feature-legal-documents-v1', {});
  logLegalLocaleToggled(
    {
      from_locale: fromLocale,
      to_locale: locale,
      pathname: returnTo,
      is_authenticated: isAuthenticated,
    },
    {
      user_id,
      session_id,
      feature_flag_active: featureFlagActive,
    },
  );

  redirect(returnTo);
}
